import { cutoutSprayFilterDefs } from "@/lib/mentor-profile/cutout-spray-params";

const WEBP_QUALITY = 0.92;
const ALPHA_MIN = 24;
const PAD = 0.08;
const WIDTH = 1080;
const HEIGHT = 1440;

const BAKE_BAND_ID = "cutoutBakeBand";
const BAKE_SPRAY_ID = "cutoutBakeSpray";
const BAKE_SOURCE_ID = "cutoutBakeSource";

function opaqueBounds(data: Uint8ClampedArray, w: number, h: number) {
  let minX = w,
    minY = h,
    maxX = 0,
    maxY = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] < ALPHA_MIN) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < minX) return { sx: 0, sy: 0, sw: w, sh: h };
  const padX = (maxX - minX) * PAD;
  const padY = (maxY - minY) * PAD;
  const sx = Math.max(0, Math.floor(minX - padX));
  const sy = Math.max(0, Math.floor(minY - padY));
  const sw = Math.min(w - sx, Math.ceil(maxX - minX + 1 + padX * 2));
  const sh = Math.min(h - sy, Math.ceil(maxY - minY + 1 + padY * 2));
  return { sx, sy, sw, sh };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = src;
  });
}

async function canvasToWebp(canvas: HTMLCanvasElement): Promise<Blob> {
  const encoded = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/webp", WEBP_QUALITY);
  });
  if (!encoded) throw new Error("webp encode failed");
  return encoded;
}

function frameCutoutOnCanvas(bitmap: ImageBitmap): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");

  const tmp = document.createElement("canvas");
  tmp.width = bitmap.width;
  tmp.height = bitmap.height;
  const tctx = tmp.getContext("2d");
  if (!tctx) throw new Error("no 2d context");
  tctx.drawImage(bitmap, 0, 0);
  const { sx, sy, sw, sh } = opaqueBounds(
    tctx.getImageData(0, 0, tmp.width, tmp.height).data,
    tmp.width,
    tmp.height,
  );

  const scale = HEIGHT / sh;
  const dw = Math.round(sw * scale);
  const dx = Math.round((WIDTH - dw) / 2);
  ctx.clearRect(0, 0, WIDTH, HEIGHT);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, sx, sy, sw, sh, dx, 0, dw, HEIGHT);
  return canvas;
}

async function bakeSprayOnCanvas(
  framed: HTMLCanvasElement,
): Promise<HTMLCanvasElement> {
  const dataUrl = framed.toDataURL("image/png");
  const filters = cutoutSprayFilterDefs(
    { band: BAKE_BAND_ID, spray: BAKE_SPRAY_ID },
    HEIGHT,
  );

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${WIDTH}" height="${HEIGHT}" viewBox="0 0 ${WIDTH} ${HEIGHT}">
  <defs>
    ${filters}
    <image id="${BAKE_SOURCE_ID}" width="${WIDTH}" height="${HEIGHT}" preserveAspectRatio="xMidYMax meet" href="${dataUrl}" xlink:href="${dataUrl}"/>
  </defs>
  <use href="#${BAKE_SOURCE_ID}" xlink:href="#${BAKE_SOURCE_ID}" filter="url(#${BAKE_BAND_ID})"/>
  <use href="#${BAKE_SOURCE_ID}" xlink:href="#${BAKE_SOURCE_ID}" filter="url(#${BAKE_SPRAY_ID})"/>
  <use href="#${BAKE_SOURCE_ID}" xlink:href="#${BAKE_SOURCE_ID}"/>
</svg>`;

  const svgUrl = URL.createObjectURL(
    new Blob([svg], { type: "image/svg+xml;charset=utf-8" }),
  );

  try {
    const img = await loadImage(svgUrl);
    const canvas = document.createElement("canvas");
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context");
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    ctx.drawImage(img, 0, 0, WIDTH, HEIGHT);
    return canvas;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

/**
 * Background removal → framed cutout with baked cyan spray (for RN match cards).
 * Dynamically imports `@imgly/background-removal`. Failures throw so the UI can retry.
 */
export async function createCutoutBlob(file: Blob): Promise<Blob> {
  const { removeBackground } = await import("@imgly/background-removal");

  let cutout: Blob;
  try {
    cutout = await removeBackground(file, {
      model: "isnet_quint8",
      device: "gpu",
      output: { format: "image/webp", quality: WEBP_QUALITY },
      rescale: true,
    });
  } catch {
    cutout = await removeBackground(file, {
      model: "isnet_quint8",
      device: "cpu",
      output: { format: "image/webp", quality: WEBP_QUALITY },
      rescale: true,
    });
  }

  const bitmap = await createImageBitmap(cutout);
  try {
    const framed = frameCutoutOnCanvas(bitmap);
    const sprayed = await bakeSprayOnCanvas(framed);
    return await canvasToWebp(sprayed);
  } finally {
    bitmap.close();
  }
}
